import React, { memo, useMemo, useEffect } from 'react';
// eslint-disable-next-line jira/import/no-restricted-import
import { useQueryBasedViewAutomaticTracking } from '@atlassian/jira-business-experience-tracking/src/controllers/experience-tracker/index.tsx';
import { ErrorFlag } from '@atlassian/jira-issue-error-flag/src/index.tsx';
import { usePreviousWithInitial } from '@atlassian/jira-platform-react-hooks-use-previous/src/common/utils/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { LOAD_GROUP_ITEMS_EXPERIENCE } from '../../../common/constants.tsx';
import { useGroupBy } from '../../group-by/index.tsx';
import { useListItemGroups } from '../../list-item-groups/index.tsx';
import messages from '../../with-grouping/messages.tsx';
import { useGroupChildrenLoader } from '../group-children-loader/index.tsx';
import { useHierarchyStore } from '../hierarchy-store/index.tsx';
import { useResetItems } from '../reset-items/index.tsx';
import { useSetHierarchyData } from '../set-hierarchy-data/index.tsx';
import {
	useListViewRefreshCacheActions,
	useListViewRefreshCacheStore,
} from '../../list-view-refresh-cache/ListViewRefreshCacheStore.tsx';

const GroupsLoader = () => {
	const items = useListItemGroups();
	const { childrenLoader } = useGroupChildrenLoader({ groups: items.data });
	const prevItems = usePreviousWithInitial(items);
	const [, { expandFirstItems }] = useHierarchyStore();
	const { setHierarchyData } = useSetHierarchyData();
	const [groupByFieldId] = useGroupBy();

	const { resetListViewGroupByRefreshCacheTrigger } = fg('refetch_status_on_list_group_by_status')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useListViewRefreshCacheActions()
		: { resetListViewGroupByRefreshCacheTrigger: undefined };

	const shouldTriggerGroupByCacheRefresh = fg('refetch_status_on_list_group_by_status')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useListViewRefreshCacheStore((state) => state.shouldTriggerGroupByCacheRefresh)
		: false;

	useQueryBasedViewAutomaticTracking(LOAD_GROUP_ITEMS_EXPERIENCE, {
		loading: items.loading,
		error: items.error,
		shouldStartExperience: true,
	});

	// resets expanded items and queryData cache when filtering/sorting/groupBy is updated
	useResetItems();

	useEffect(() => {
		if (shouldTriggerGroupByCacheRefresh) {
			items.refetch();
			resetListViewGroupByRefreshCacheTrigger?.();
		}
	}, [items, resetListViewGroupByRefreshCacheTrigger, shouldTriggerGroupByCacheRefresh]);

	// initialize items when switching between grouping -> hierarchy
	useEffect(() => {
		if (items.loading) {
			return;
		}
		setHierarchyData(items);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [items.loading]);

	// updates groups only when "data", "loading", "isLoadingNextPage" or "totalCount" changes
	useEffect(() => {
		if (
			prevItems.totalCount !== items.totalCount ||
			prevItems.loading !== items.loading ||
			prevItems.data !== items.data ||
			prevItems.isLoadingNextPage !== items.isLoadingNextPage
		) {
			setHierarchyData(items);
		}
	}, [items, prevItems, setHierarchyData]);

	useEffect(() => {
		if (!items.loading && groupByFieldId != null) {
			expandFirstItems();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [items.loading, groupByFieldId]);

	// avoid re-fetching children when GroupsLoader re-renders
	const groupChildrenLoader = useMemo(() => childrenLoader, [childrenLoader]);

	return (
		<>
			{groupChildrenLoader}
			{items.error && (
				<ErrorFlag
					error={items.error}
					title={messages.errorTitle}
					description={messages.errorMessage}
				/>
			)}
		</>
	);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default memo<Record<any, any>>(GroupsLoader);
